import React, { useState } from "react";

import { Button } from "src/base-components/Button";
import { CodeEditor } from "src/base-components/CodeInput/CodeEditor";
import { Tabs } from "src/base-components/Tabs";
import { Modal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import {
  JinjaFile,
  useUploadCompiledManifest,
} from "src/flowContainer/hooks/useManifestBuilder";

type DebugManifestModalProps = {
  open: boolean;
  onClose: () => void;
  afterLeave?: () => void;
  baseUrl: string;
  flowUrl: string;
  initialManifest: Record<string, unknown>;
  jinjaFile: JinjaFile | null;
  testCaseRows: Record<string, unknown>[] | null;
  sampleResponseRows: Record<string, unknown>[] | null;
};

export const DebugManifestModal: React.FC<DebugManifestModalProps> = ({
  open,
  onClose,
  afterLeave,
  baseUrl,
  flowUrl,
  initialManifest,
  jinjaFile,
  testCaseRows,
  sampleResponseRows,
}) => {
  const [manifestJson, setManifestJson] = useState(
    JSON.stringify(initialManifest, null, 2),
  );
  const { mutateAsync: uploadCompiledManifest, isPending } =
    useUploadCompiledManifest();

  const handleSave = async () => {
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(manifestJson) as Record<string, unknown>;
    } catch {
      toastActions.failure({
        title: "Invalid JSON",
        description: "Please fix the JSON syntax before saving.",
      });
      return;
    }

    try {
      await uploadCompiledManifest({
        baseUrl,
        manifest: parsed,
        flowUrl,
        sampleResponseRows,
        jinjaFile,
      });
      onClose();
    } catch {
      // Error toast is shown by the mutation's onError handler
    }
  };

  const tabs = [
    {
      key: "manifest",
      label: "Manifest",
      content: (
        <div className="h-[500px]">
          <CodeEditor
            dataLoc="debug-manifest-editor"
            language="json"
            lineWrapping
            value={manifestJson}
            onChange={setManifestJson}
          />
        </div>
      ),
    },
    ...(jinjaFile
      ? [
          {
            key: "resource-body",
            label: `Resource Body (${jinjaFile.name})`,
            content: (
              <div className="h-[500px]">
                <CodeEditor
                  dataLoc="debug-manifest-jinja-editor"
                  language={undefined}
                  lineWrapping
                  readOnly
                  value={jinjaFile.content}
                />
              </div>
            ),
          },
        ]
      : []),
    ...(testCaseRows && testCaseRows.length > 0
      ? [
          {
            key: "test-cases",
            label: `Test Cases (${testCaseRows.length})`,
            content: (
              <div className="h-[500px]">
                <CodeEditor
                  dataLoc="debug-manifest-test-cases"
                  language="json"
                  lineWrapping
                  readOnly
                  value={JSON.stringify(testCaseRows, null, 2)}
                />
              </div>
            ),
          },
        ]
      : []),
    ...(sampleResponseRows && sampleResponseRows.length > 0
      ? [
          {
            key: "sample-responses",
            label: `Sample Responses (${sampleResponseRows.length})`,
            content: (
              <div className="h-[500px]">
                <CodeEditor
                  dataLoc="debug-manifest-sample-responses"
                  language="json"
                  lineWrapping
                  readOnly
                  value={JSON.stringify(sampleResponseRows, null, 2)}
                />
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <Modal afterLeave={afterLeave} open={open} size="lg" onClose={onClose}>
      <Modal.Header description="Inspect and edit the compiled manifest JSON, then upload it.">
        Debug Manifest
      </Modal.Header>
      <Modal.Content noScroll>
        <Tabs tabs={tabs} />
      </Modal.Content>
      <Modal.Footer
        primaryButton={
          <Button
            dataLoc="debug-manifest-save"
            disabled={isPending}
            loading={isPending}
            variant="primary"
            onClick={handleSave}
          >
            Upload Manifest
          </Button>
        }
        secondaryButton="Cancel"
      />
    </Modal>
  );
};
